import React, { useEffect, useState } from "react";
import { Row, Container, Col, Card, CardHeader, CardBody } from "reactstrap";
import { useTranslation } from "react-i18next";
import SearchInput from "../Common/SearchInput";
import { resetFormPromptState } from "src/recoil/atoms/global";
import useUrlState from "@ahooksjs/use-url-state";
import { useRecoilState } from "recoil";
import FormSelect from "../Common/Form/FormSelect";
import TableCommon from "../Common/Table";
import { useForm } from "react-hook-form";
import { handleScrollTo, handleScrollToTop } from "src/utils/common";
import { columns } from "./columns";
import { PAGINATION_DEFAULT } from "src/constant/common";
import DownloadIcon from "src/assets/images/download.png";
import useGetSequenceList from "./useGetSequenceList";
import ModalDownloadSequence from "../Common/Modal/ModalDownloadSequence";
import isEmpty from "lodash/isEmpty";
import ModalAddTagToSequence from "../Common/Modal/ModalAddTagToSequence";
import useGetProjectInstanceList from "src/components/ProjectInstance/useGetProjectInstanceList";
import useGetTestPackageList from "src/components/TestPackage/useGetTestPackageList";

const FORM_NAME = {
  CURRENT_PROJECT_INSTANCE: "currentProjectInstance",
  CURRENT_TEST_PACKAGE: "currentTestPackage",
};

const SequenceList = () => {
  const { t } = useTranslation("sequence");
  const [resetForm, setResetForm] = useRecoilState(resetFormPromptState);
  const [isSequenceDownloadModalOpen, setIsSequenceDownloadModalOpen] = useState(false);
  const [isAddTagToSequenceModalOpen, setIsAddTagToSequenceModalOpen] = useState(false);
  const [allowScrollToId, setAllowScrollToId] = useState(true);
  const [isLoadingSequence, setIsLoadingSequence] = useState(true);
  const [isLoadingProjectInstance, setIsLoadingProjectInstance] = useState(true);
  const [isLoadingTestPackage, setIsLoadingTestPackage] = useState(false);
  const [sequenceSelected, setSequenceSelected] = useState(null);
  const [multiSequencesDownload, setMultiSequencesDownload] = useState([]);
  const [singleSequenceDownload, setSingleSequenceDownload] = useState([]);
  const [params, setParams] = useUrlState(
    {},
    {
      navigateMode: "replace",
      stringifyOptions: {
        skipNull: true,
        skipEmptyString: true,
      },
    }
  );

  const [textSearch, setTextSearch] = useState(params?.condition || null);
  const { control, setValue } = useForm({
    defaultValues: {
      [FORM_NAME.CURRENT_PROJECT_INSTANCE]: params?.instanceId || null,
      [FORM_NAME.CURRENT_TEST_PACKAGE]: params?.testPackageId || null,
    },
  });

  const { projectInstanceList: instanceOptions } = useGetProjectInstanceList(
    {
      isSelectOptions: true,
      refetchInterval: false,
      refetchOnWindowFocus: false,
    },
    () => setIsLoadingProjectInstance(false)
  );
  const { testPackageList: testPackageOptions } = useGetTestPackageList(
    {
      isSelectOptions: true,
      yamcs_package_id: params?.instanceId,
      enabled: !!params?.instanceId,
    },
    () => setIsLoadingTestPackage(false)
  );

  const instanceAndTestPackageFetched = !isLoadingProjectInstance && !isLoadingTestPackage;

  const { sequenceList, sequenceListRefetch } = useGetSequenceList({
    page: params?.page,
    limit: PAGINATION_DEFAULT.pageSize,
    q: params?.condition,
    yamcs_package_id: params?.instanceId,
    test_package_id: params?.testPackageId,
    order_by: "-updated_at",
    onSettledCallback: () => setIsLoadingSequence(false),
  });

  const onKeyDownInput = (e) => {
    if (e.key === "Enter") {
      onSubmitSearch(e.target.value);
    }
  };
  const onSubmitSearch = (textSearch) => {
    if (params?.condition === textSearch && !isLoadingSequence) {
      setIsLoadingSequence(true);
    }
    setParams({
      page: 1,
      condition: textSearch,
    });
  };

  const handleCloseModalDownloadSequence = () => {
    setIsSequenceDownloadModalOpen(false);
    setSingleSequenceDownload([]);
  };

  const handleCloseModalAddTagToSequence = () => {
    setIsAddTagToSequenceModalOpen(false);
  };

  const getCurrentPage = () => {
    return (params?.page || PAGINATION_DEFAULT.page) - 1;
  };

  const onChangePage = (value) => {
    setParams({
      ...params,
      page: value,
    });
    handleScrollToTop();
  };

  const handleChangeProjectInstance = (selectOption) => {
    if (!selectOption) {
      setIsLoadingTestPackage(false);
    } else {
      setIsLoadingTestPackage(true);
    }

    setParams({
      ...params,
      page: 1,
      instanceId: selectOption || null,
      testPackageId: null,
    });
    setValue(FORM_NAME.CURRENT_TEST_PACKAGE, null);
  };

  const handleChangeTestPackage = (selectOption) => {
    setParams({
      ...params,
      page: 1,
      testPackageId: selectOption || null,
    });
  };

  const handleAddSequenceTag = (sequenceData) => {
    setSequenceSelected(sequenceData);
    setIsAddTagToSequenceModalOpen(true);
  };

  const handleSequenceDownload = (sequenceData) => {
    setSingleSequenceDownload([sequenceData]);
    setIsSequenceDownloadModalOpen(true);
  };

  const handleClickRenderSequenceVisualize = (sequenceData) => {};

  const handleAddSequenceTagSuccess = async () => {
    handleScrollToTop();

    setParams({
      ...params,
      page: 1,
    });

    if (Number(params?.page) === 1 || params?.page === undefined) {
      setIsLoadingSequence(true);
      await sequenceListRefetch();
    }
  };

  useEffect(() => {
    if (!isLoadingProjectInstance) {
      setIsLoadingTestPackage(false);
    }
  }, [isLoadingProjectInstance]);

  useEffect(() => {
    if (resetForm) {
      setTextSearch(null);
      setResetForm(false);
      handleScrollToTop();
    }
  }, [resetForm]);

  useEffect(() => {
    if (allowScrollToId) {
      handleScrollTo(0, 90, `row${params.id}`);
      setAllowScrollToId(false);
    }
  }, [allowScrollToId]);

  useEffect(() => {
    setIsLoadingSequence(true);
    if (!params?.instanceId) {
      setValue(FORM_NAME.CURRENT_PROJECT_INSTANCE, null);
      setValue(FORM_NAME.CURRENT_TEST_PACKAGE, null);
    }
  }, [params]);

  return (
    <Container fluid>
      <Row>
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h2 className="header-title mb-0">{t("list.title")}</h2>
                <SearchInput
                  submit={onSubmitSearch}
                  setTextSearch={setTextSearch}
                  onKeyDownInput={onKeyDownInput}
                  textSearch={textSearch}
                  placeholder={t("list.search_placeholder")}
                />
              </div>
              <Row>
                <Col md={12}>
                  <Card>
                    <CardHeader className="bg-white border-0 d-flex align-items-center justify-content-start">
                      <FormSelect
                        name={FORM_NAME.CURRENT_PROJECT_INSTANCE}
                        classGroup="mb-0 width-by-px-300 m-r-10"
                        control={control}
                        placeholder={t("list.filter_by_project_instance_placeholder")}
                        onChangeExtra={handleChangeProjectInstance}
                        isClearable
                        isSearchable
                        options={instanceAndTestPackageFetched || instanceOptions ? instanceOptions : []}
                        isLoading={isLoadingProjectInstance}
                      />
                      <div>
                        <i className="bi bi-chevron-right text-dark f-s-20"></i>
                      </div>
                      <FormSelect
                        name={FORM_NAME.CURRENT_TEST_PACKAGE}
                        classGroup="mb-0 width-by-px-300 m-x-10"
                        control={control}
                        placeholder={t("list.filter_by_test_package_placeholder")}
                        onChangeExtra={handleChangeTestPackage}
                        options={instanceAndTestPackageFetched ? testPackageOptions : []}
                        noOptionsMessage={params?.instanceId ? "No options" : "Please select a Project Instance first"}
                        isClearable
                        isSearchable
                        isLoading={isLoadingProjectInstance || isLoadingTestPackage}
                      />
                      <button
                        className="btn btn-primary text-capitalize height-by-px-38 p-t-0 p-b-0 m-x-20 text-nowrap d-flex align-items-center justify-content-center"
                        onClick={() => setIsSequenceDownloadModalOpen(true)}
                        disabled={!multiSequencesDownload?.length}
                      >
                        <img src={DownloadIcon} className="width-by-px-24 text-white-filter m-r-8" alt="Download icon" />
                        {t("list.batch_download")}
                      </button>
                    </CardHeader>
                    <CardBody className="bg-white">
                      <TableCommon
                        loading={isLoadingSequence}
                        columns={columns({ t, handleSequenceDownload, handleClickRenderSequenceVisualize, handleAddSequenceTag, params })}
                        data={sequenceList?.items}
                        onChangePage={onChangePage}
                        tableFixed
                        tableClassName="table striped table-collapse table-fixed sequences-table"
                        customClassName="overflow-auto simulation-table"
                        pageCurrent={getCurrentPage()}
                        pageCount={sequenceList?.pagination?.total_page}
                        totalItem={sequenceList?.pagination?.total}
                        pageSize={PAGINATION_DEFAULT.pageSize}
                        enableRowSelection={!isEmpty(sequenceList?.items)}
                        onRowSelectionChange={(sequenceData) => setMultiSequencesDownload(sequenceData)}
                      />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Row>
      {isSequenceDownloadModalOpen && (
        <ModalDownloadSequence
          sequencesData={isEmpty(singleSequenceDownload) ? multiSequencesDownload : singleSequenceDownload}
          isOpen={isSequenceDownloadModalOpen}
          toggle={handleCloseModalDownloadSequence}
        />
      )}
      {isAddTagToSequenceModalOpen && (
        <ModalAddTagToSequence
          sequenceData={sequenceSelected}
          isOpen={isAddTagToSequenceModalOpen}
          toggle={handleCloseModalAddTagToSequence}
          handleAddSequenceTagSuccess={handleAddSequenceTagSuccess}
          setIsLoadingSequence={setIsLoadingSequence}
        />
      )}
    </Container>
  );
};

export default SequenceList;
